import { RefreshError } from './RefreshError';
import { LoadingIndicator } from './LoadingIndicator';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type {
  ColDef,
  SelectionChangedEvent,
  GetRowIdParams,
  RowDataUpdatedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useEvalSets, type EvalSetItem } from '../hooks/useEvalSets';
import { ErrorDisplay } from './ErrorDisplay';
import { TimeAgoCellRenderer, CopyButton } from './ag-grid/cellRenderers';
import { PageHeader } from './PageHeader';
import { ListSkeleton } from './ListSkeleton';
import { Pagination } from './Pagination';
import { useClampPage } from '../hooks/useClampPage';
import { useViewers } from '../hooks/useViewers';
import { viewerLinkTarget } from '../utils/viewers';
import { OpenInViewerMenu } from './OpenInMenu';
import { useGridLayout } from '../hooks/useGridLayout';
import { ResetGridLayoutButton } from './ag-grid/ResetGridLayoutButton';
import './ag-grid/styles.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 50;
// Two-line identity cells (id over tasks and creator).
const ROW_HEIGHT = 52;

// Selection survives leaving and returning to the list (per tab), so a set of
// eval sets picked for "View samples" is still picked after a look at one.
const SELECTION_KEY = 'hawk:eval-sets:selected';

function readSelection(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SELECTION_KEY) ?? '[]'));
  } catch {
    return new Set();
  }
}

function writeSelection(ids: string[]): void {
  try {
    sessionStorage.setItem(SELECTION_KEY, JSON.stringify(ids));
  } catch {
    // storage unavailable: selection just won't survive navigation
  }
}

// Identity on two lines: the id (with copy) and, beneath it, what ran and who
// ran it. Recognising an eval set needs the whole id and the task names, which
// equal-width columns never had room for.
function EvalSetCell({ data }: { data: EvalSetItem }) {
  const tasks = data.task_names?.join(', ') || 'no tasks recorded';
  const { primaryUrl } = useViewers();
  const target = { level: 'eval_set', evalSetId: data.eval_set_id } as const;
  const href = primaryUrl(target);
  return (
    <span className="flex w-full min-w-0 flex-col justify-center leading-tight">
      <span className="copy-button-cell">
        {/* Plain href: opens the embedded viewer, which needs a full load. */}
        <a
          href={href}
          {...viewerLinkTarget(href)}
          className="copy-button-cell-text text-sm font-medium text-ink no-underline hover:text-accent hover:underline"
          title={data.eval_set_id}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {data.eval_set_id}
        </a>
        <CopyButton value={data.eval_set_id} />
        <OpenInViewerMenu
          target={target}
          subject={data.eval_set_id}
          className="ml-1"
        />
      </span>
      <span
        className="truncate text-xs text-muted"
        title={data.created_by ? `${tasks} · by ${data.created_by}` : tasks}
      >
        {tasks}
        {data.created_by && (
          <>
            {' · by '}
            <span className="text-ink-2">{data.created_by}</span>
          </>
        )}
      </span>
    </span>
  );
}

export function EvalSetList() {
  const gridRef = useRef<AgGridReact<EvalSetItem>>(null);
  const [selectedEvalSets, setSelectedEvalSets] = useState<EvalSetItem[]>([]);
  // Search and page live in the URL so they survive opening an eval set and
  // coming back, reloads, and sharing.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') ?? '';
  const rawPage = parseInt(searchParams.get('page') ?? '1', 10);
  const currentPage = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;

  const {
    evalSets,
    isLoading,
    hasData,
    error,
    total,
    page,
    setPage,
    setSearch,
    refetch,
  } = useEvalSets({
    page: currentPage,
    limit: PAGE_SIZE,
    search: searchQuery,
  });
  // The hook seeds itself from the URL on mount; keep it following the URL.
  useEffect(() => {
    setSearch(searchQuery);
    setPage(currentPage);
  }, [searchQuery, currentPage, setSearch, setPage]);

  const updateParams = useCallback(
    (next: { search?: string; page?: number }) => {
      setSearchParams(
        prev => {
          const out = new URLSearchParams(prev);
          if (next.search !== undefined) {
            if (next.search) out.set('search', next.search);
            else out.delete('search');
          }
          if (next.page !== undefined) {
            if (next.page > 1) out.set('page', String(next.page));
            else out.delete('page');
          }
          return out;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Skeleton only for the first load; refetches keep the grid (and selection).
  const hasLoaded = hasData;

  const viewSamplesHref = useMemo(() => {
    if (selectedEvalSets.length === 0) return undefined;
    const combinedIds = selectedEvalSets.map(es => es.eval_set_id).join(',');
    return `/eval-set/${encodeURIComponent(combinedIds)}#/samples/`;
  }, [selectedEvalSets]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateParams({ page: newPage });
      // Clear selection when changing pages
      setSelectedEvalSets([]);
      writeSelection([]);
      if (gridRef.current?.api) {
        gridRef.current.api.deselectAll();
      }
    },
    [updateParams]
  );

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const displayPage = page || currentPage;
  const clampTo = useCallback(
    (next: number) => updateParams({ page: next }),
    [updateParams]
  );
  useClampPage(
    currentPage,
    totalPages,
    hasData && !isLoading && !error,
    clampTo
  );

  const columnDefs = useMemo<ColDef<EvalSetItem>[]>(
    () => [
      {
        field: 'eval_set_id',
        headerName: 'Eval set',
        // A fixed width rather than `flex: 1`: eval set ids run to about 45
        // characters, and letting the column soak up every spare pixel made
        // it dominate a wide window for no gain. Wide enough for the longest
        // id with its copy button; users can drag it, and useGridLayout
        // remembers where they put it.
        width: 400,
        minWidth: 300,
        cellRenderer: EvalSetCell,
      },
      {
        field: 'eval_count',
        headerName: 'Evals',
        width: 90,
        type: 'rightAligned',
      },
      {
        field: 'latest_eval_created_at',
        headerName: 'Latest activity',
        width: 150,
        cellRenderer: TimeAgoCellRenderer,
      },
      {
        // An explicit id on the field-less column, so the persisted layout
        // keeps referring to it if these definitions are reordered.
        colId: 'clone',
        headerName: '',
        width: 90,
        sortable: false,
        cellRenderer: (params: { data: EvalSetItem }) => (
          <a
            href={`/launch?clone=${encodeURIComponent(params.data.eval_set_id)}`}
            className="row-action row-action-accent"
            title="Start a new eval set from this one's config"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            Clone
          </a>
        ),
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef<EvalSetItem>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
    }),
    []
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<EvalSetItem>) => params.data.eval_set_id,
    []
  );

  const layout = useGridLayout('eval-sets');

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<EvalSetItem>) => {
      const selected = event.api.getSelectedRows();
      setSelectedEvalSets(selected);
      writeSelection(selected.map(es => es.eval_set_id));
    },
    []
  );

  // Once, when rows first arrive: re-select what was selected before leaving.
  const restoredSelection = useRef(false);
  const onRowDataUpdated = useCallback(
    (event: RowDataUpdatedEvent<EvalSetItem>) => {
      if (restoredSelection.current) return;
      const remembered = readSelection();
      if (remembered.size === 0) return;
      let any = false;
      event.api.forEachNode(node => {
        if (node.data && remembered.has(node.data.eval_set_id)) {
          node.setSelected(true);
          any = true;
        }
      });
      if (any) restoredSelection.current = true;
    },
    []
  );

  if (error && !hasData) {
    return <ErrorDisplay message={error.toString()} />;
  }

  return (
    <div className="pane flex h-full flex-col overflow-hidden">
      <PageHeader
        title="Eval Sets"
        description="Results in the warehouse: one row per eval set, with what ran and who ran it. Select several to open their samples together."
        count={hasLoaded ? total.toLocaleString() : undefined}
        actions={
          viewSamplesHref ? (
            <a href={viewSamplesHref} className="btn btn-ghost no-underline">
              View Samples ({selectedEvalSets.length})
            </a>
          ) : (
            <button
              type="button"
              disabled
              className="btn btn-ghost"
              title="Select eval sets to open their samples together"
            >
              View Samples
            </button>
          )
        }
      >
        <form
          onSubmit={e => e.preventDefault()}
          className="relative min-w-40 max-w-sm flex-1"
        >
          <input
            type="search"
            aria-label="Search eval sets"
            placeholder="Search eval sets…"
            value={searchQuery}
            onChange={e => {
              updateParams({ search: e.target.value, page: 1 });
              setSelectedEvalSets([]);
              writeSelection([]);
              if (gridRef.current?.api) {
                gridRef.current.api.deselectAll();
              }
            }}
            className="input h-7 w-full"
          />
          <LoadingIndicator loading={isLoading} />
        </form>
      </PageHeader>

      <RefreshError error={error} onRetry={refetch} />
      {/* AG Grid */}
      <div className="flex-1 overflow-hidden relative">
        {/* An empty page while the next one loads is not "no results". */}
        {!hasLoaded || (evalSets.length === 0 && isLoading) ? (
          <ListSkeleton
            columns={['w-8', 'w-48', 'w-48', 'w-32', 'w-24', 'flex-1']}
          />
        ) : evalSets.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted">
            <p>
              {searchQuery
                ? `No eval sets match "${searchQuery}".`
                : 'No eval sets yet.'}
            </p>
            <p className="mt-1 text-xs">
              Eval sets appear here once a run&apos;s logs have been imported
              into the warehouse; running jobs are on the Jobs page.
            </p>
          </div>
        ) : (
          <div className="ag-theme-quartz relative h-full w-full">
            <ResetGridLayoutButton layout={layout} />
            <AgGridReact<EvalSetItem>
              {...layout.gridProps}
              ref={gridRef}
              rowData={evalSets}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              rowSelection={{
                mode: 'multiRow',
                headerCheckbox: true,
                checkboxes: true,
                // Checkboxes select; the id opens the eval set. Clicking
                // elsewhere in a row does nothing surprising.
                enableClickSelection: false,
              }}
              onSelectionChanged={onSelectionChanged}
              onRowDataUpdated={onRowDataUpdated}
              rowHeight={ROW_HEIGHT}
              animateRows={false}
              suppressCellFocus={true}
              domLayout="normal"
            />
          </div>
        )}
      </div>

      <Pagination
        page={displayPage}
        totalPages={totalPages}
        total={total}
        pageSize={PAGE_SIZE}
        disabled={isLoading}
        onChange={handlePageChange}
      />
    </div>
  );
}
